<template>
  <div
    ref="dueDatePopup"
    class="date-picker-popup calendar"
    :class="{ 'w-fit': onlyTimePicker }"
    @click.stop="focusInput"
  >
    <div
      v-if="!onlyTimePicker"
      class="header"
    >
      <div class="selected-month-year">{{ curMonthString }} {{ curYear }}</div>
      <div class="arrows">
        <em
          class="custom-icons pt"
          :style="[getCustomIconStyle(iconPath('chevron-left'))]"
          @click="changeCurMonth(-1)"
        />
        <em
          class="custom-icons pt"
          :style="[getCustomIconStyle(iconPath('chevron-right'))]"
          @click="changeCurMonth(1)"
        />
        <em
          v-if="!disableDelete"
          class="custom-icons pt delete-icon"
          :style="[getCustomIconStyle(iconPath('trash'))]"
          @click="deleteDate"
        />
      </div>
    </div>
    <div
      v-if="!onlyTimePicker"
      class="calendar-grid-wrap"
    >
      <input
        :ref="'datePicker'"
        v-autofocus
        readonly="true"
        class="hidden-input"
        data-hidden-input="true"
        @keydown.stop="handleKeyDown"
      />
      <div
        v-for="day in daysLabel"
        :key="day"
        class="day-label"
      >
        {{ day }}
      </div>
      <div
        v-for="date in dates"
        :key="date.id"
        class="day pt"
        :class="{
          'other-month': date.getMonth() !== curMonth,
          selected: isSelected(date),
        }"
        :data-focused-date="convertDateToString(date) === datePickerSelectedDate"
        @click="handleAddDate(date)"
      >
        {{ date.getDate() }}
      </div>
    </div>
    <div
      v-if="onlyTimePicker"
      class="time-picker-section"
    >
      <div class="time-inputs">
        <div class="time-input-group">
          <label>Hours</label>
          <input
            v-model="formattedHours"
            v-autofocus
            class="time-input"
            type="text"
            min="0"
            max="23"
            @input="handleTimeChange"
            @keydown="handleTimeKeyDown"
            @keypress="handleTimeKeypress"
          />
        </div>
        <div class="time-separator">:</div>
        <div class="time-input-group">
          <label>Minutes</label>
          <input
            v-model="formattedMinutes"
            type="text"
            class="time-input"
            min="0"
            max="59"
            @input="handleTimeChange"
            @keydown="handleTimeKeyDown"
            @keypress="handleTimeKeypress"
          />
        </div>
      </div>
      <em
        class="custom-icons pt delete-icon"
        :style="[getCustomIconStyle(iconPath('trash'))]"
        @click="deleteTime"
      />
    </div>
  </div>
</template>

<script>
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import {
  addMonths,
  subMonths,
  endOfMonth,
  startOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameDay,
  parse,
  getUnixTime,
  getYear,
  getMonth,
  fromUnixTime,
  isValid,
} from 'date-fns';

const date = new Date();

const msPerSecond = 1000;
const msPerMinute = 60 * msPerSecond;
const msPerHour = msPerMinute * 60;
const msPerDay = msPerHour * 24;

export default {
  name: 'DateTimePicker',
  props: {
    currentCardId: {
      type: String,
      default: '',
    },
    property: {
      type: String,
      default: 'due_date',
    },
    selectedDate: {
      type: Number,
      default: 0,
    },
    selectedTime: {
      type: Number,
      required: false,
      default: 0,
    },
    disableDelete: {
      type: Boolean,
      default: false,
    },
    onlyTimePicker: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close-popover', 'dueDate', 'esc', 'time-change'],
  setup() {
    const { iconPath, getCustomIconStyle } = useCustomIcon();
    return {
      iconPath,
      getCustomIconStyle,
    };
  },
  data() {
    return {
      datePickerSelectedDate: '',
      pickedDate: new Date(),
      curYear: getYear(date),
      curMonth: getMonth(date),
      selectedReminderIndex: -1,
      dates: [],
      focusTimeout: null,
      selectedHours: 0,
      selectedMinutes: 0,
    };
  },
  computed: {
    curMonthString() {
      return this.translate('months').split(',')[this.curMonth];
    },
    daysLabel() {
      return this.translate('daysAbbreviation').split(',');
    },
    formattedHours: {
      get() {
        return this.selectedHours.toString();
      },
      set(value) {
        this.selectedHours = parseInt(value, 10) || 0;
      },
    },
    formattedMinutes: {
      get() {
        return this.selectedMinutes.toString().padStart(2, '0');
      },
      set(value) {
        this.selectedMinutes = parseInt(value, 10) || 0;
      },
    },
    reminderItems() {
      return [
        {
          id: 'none',
          label: this.translate('none'),
          selectedItem: false,
        },
        {
          id: '1week',
          label: `1 ${this.translate('weekBefore')}`,
          selectedItem: false,
          value: msPerDay * 7,
        },
        {
          id: '2days',
          label: `2 ${this.translate('daysBefore')}`,
          selectedItem: false,
          value: msPerDay * 2,
        },
        {
          id: '1day',
          label: `1 ${this.translate('dayBefore')}`,
          selectedItem: false,
          value: msPerDay,
        },
        {
          id: '8hours',
          label: `8 ${this.translate('hoursBefore')}`,
          selectedItem: false,
          value: msPerHour * 8,
        },
        {
          id: '1hour',
          label: `1 ${this.translate('hourBefore')}`,
          selectedItem: false,
          value: msPerHour,
        },
        {
          id: 'ontime',
          label: this.translate('onTime'),
          selectedItem: false,
          value: 0,
        },
      ];
    },
    currentCard() {
      return this.$store.getters.getCardById(this.currentCardId);
    },
    dueDateTimeTimestamp() {
      if (!this.pickedDate) {
        return 0;
      }
      const dateTime = new Date(this.pickedDate);
      dateTime.setHours(9); // due dates should always be set as 9AM local time
      dateTime.setMinutes(0);
      return getUnixTime(dateTime);
    },
    pickedReminderTimestamp() {
      if (this.selectedReminderIndex === 0) return '';
      return (
        this.dueDateTimeTimestamp - this.reminderItems[this.selectedReminderIndex || 0]?.value ||
        this.reminderItems[0].value
      );
    },

    currentCardDueDate() {
      return this.currentCard[this.property];
    },
  },
  watch: {
    draggingIsOn(newValue) {
      if (newValue) {
        this.closePopup();
      }
    },
    currentCardDueDate() {
      const d = fromUnixTime(this.currentCard[this.property]);
      this.pickDate(d);
      this.setTimeToZero();
      this.setInitialTime();
      this.setDueReminder();
    },
    curMonth(newVal) {
      if (newVal >= 0 && newVal < 12) {
        this.calculateDates();
      }
    },
  },
  beforeMount() {
    if (this.currentCard[this.property] && this.currentCard[this.property] !== 0) {
      const d = fromUnixTime(this.currentCard[this.property]);
      this.pickDate(d);
      this.datePickerSelectedDate = this.convertDateToString(d);
    } else if (this.selectedDate !== 0) {
      const d = fromUnixTime(this.selectedDate);
      this.pickDate(d);
      this.datePickerSelectedDate = this.convertDateToString(d);
    } else {
      this.datePickerSelectedDate = this.convertDateToString(new Date());
    }
    this.setTimeToZero();
    this.setInitialTime();
    this.setDueReminder();
  },
  mounted() {
    this.$nextTick(() => {
      this.focusInput();
    });
  },
  beforeUnmount() {
    clearTimeout(this.focusTimeout);
  },
  created() {
    this.calculateDates();
  },
  methods: {
    calculateDates() {
      const initialDate = new Date(this.curYear, this.curMonth);
      const date = isValid(initialDate) ? initialDate : new Date();

      const firstDayOfMonth = startOfMonth(date);
      const startDate = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 }); // Monday

      const lastDayOfMonth = endOfMonth(date);
      const endDate = endOfWeek(lastDayOfMonth, { weekStartsOn: 1 }); // Monday

      this.dates = eachDayOfInterval({ start: startDate, end: endDate });
    },

    changeCurMonth(n) {
      const newDate =
        n > 0
          ? addMonths(new Date(this.curYear, this.curMonth), n)
          : subMonths(new Date(this.curYear, this.curMonth), Math.abs(n));

      this.curMonth = getMonth(newDate);
      this.curYear = getYear(newDate);
    },
    pickDate(d) {
      this.pickedDate = d;
      this.curMonth = getMonth(d);
      this.curYear = getYear(d);
    },
    deleteDate() {
      this.$emit('dueDate', 0, 0, this.property, this.currentCardId);
      this.closePopup();
    },
    deleteTime() {
      this.$emit('time-change', null, null);
      this.closePopup();
    },
    closePopup() {
      this.$emit('close-popover');
    },
    setInitialTime() {
      const selectedTime = fromUnixTime(this.selectedTime);
      this.selectedHours = selectedTime.getHours();
      this.selectedMinutes = selectedTime.getMinutes();
    },
    handleReminderPick(reminderItem, index) {
      this.selectedReminderIndex = index;
      this.setDueDate();
    },
    handleAddDate(d) {
      this.pickDate(d);
      this.setDueDate();
      this.closePopup();
      this.datePickerSelectedDate = '';
    },
    setDueDate() {
      if (!this.pickedDate) return;
      this.$emit(
        'dueDate',
        this.dueDateTimeTimestamp,
        this.pickedReminderTimestamp,
        this.property,
        this.currentCardId
      );
    },
    isSelected(dateInCalendar) {
      return isSameDay(this.pickedDate, dateInCalendar);
    },
    setDueReminder() {
      if (this.currentCard.dueDateReminder !== '') {
        const diff = this.currentCard[this.property] - this.currentCard.dueDateReminder;
        const index = this.reminderItems.findIndex((item) => item.value === diff);
        this.selectedReminderIndex = index;
        return;
      }
      this.selectedReminderIndex = 0;
    },
    setTimeToZero() {
      this.pickedDate.setHours(0);
      this.pickedDate.setMinutes(0);
      this.pickedDate.setSeconds(0);
      this.pickedDate.setMilliseconds(0);
    },

    getSelectedDateIndex() {
      return this.dates.findIndex(
        (d) => this.convertDateToString(d) === this.datePickerSelectedDate
      );
    },
    handleKeyDown(event) {
      if (this.onlyTimePicker) return;

      const key = event.key;
      switch (key) {
        case 'Tab':
          event.preventDefault();
          break;
        case 'Enter':
          event.preventDefault();
          this.handleAddDate(this.dates[this.getSelectedDateIndex()]);
          break;
        case 'Escape':
          event.preventDefault();
          this.$emit('esc');
          break;
        case 'ArrowLeft':
          if (this.convertDateToString(this.dates[0]) === this.datePickerSelectedDate) {
            this.changeCurMonth(-1); // move to previous month
          }
          this.changeDate(-1, 0); // move to previous day
          break;
        case 'ArrowUp':
          if (this.getSelectedDateIndex() < 7) return;
          this.changeDate(0, -7); // move to previous week
          break;
        case 'ArrowRight':
          if (
            this.convertDateToString(this.dates[this.dates.length - 1]) ===
            this.datePickerSelectedDate
          ) {
            this.changeCurMonth(1); // move to next month
          }
          this.changeDate(1, 0); // move to next day
          break;
        case 'ArrowDown':
          if (this.getSelectedDateIndex() > this.dates.length - 8) return;
          this.changeDate(0, 7); // move to next week
          break;
      }
    },
    changeDate(daysOffset, weekOffset) {
      let selectedDate;
      if (this.datePickerSelectedDate === '') {
        selectedDate = new Date();
      } else {
        selectedDate = parse(this.datePickerSelectedDate, 'dd/MM/yyyy', new Date());
      }

      selectedDate.setDate(selectedDate.getDate() + daysOffset + weekOffset);
      this.datePickerSelectedDate = this.convertDateToString(selectedDate);
    },

    focusInput() {
      this.focusTimeout = setTimeout(() => {
        this.datePickerSelectedDate = this.convertDateToString(this.pickedDate);
        this.$refs.dueDatePopup?.focus();
        this.$refs.datePicker?.focus();
        this.$refs.datePicker?.select();
      }, 300);
    },

    convertDateToString(d) {
      return format(d, 'dd/MM/yyyy');
    },
    handleTimeKeyDown(e) {
      if (['Escape', 'Enter'].includes(e.key)) this.$emit('esc');
    },
    handleTimeChange() {
      // Ensure hours are between 0-23
      this.selectedHours = Math.min(this.selectedHours, 23);
      // Ensure minutes are between 0-59
      this.selectedMinutes = Math.min(this.selectedMinutes, 59);

      this.$emit('time-change', this.selectedHours, this.selectedMinutes);
    },
    handleTimeKeypress(e) {
      // Disallow non-numeric characters
      if (!/[0-9]/.test(e.key)) e.preventDefault();
    },
  },
};
</script>

<style lang="scss">
$selectedColor: $stBlue;

.date-picker-popup-wrap {
  margin-top: -22px !important;
  margin-right: 8px !important;
}

.date-picker-popup-bottom {
  margin-top: 5px !important;
  margin-right: 8px !important;
}

.calendar {
  pointer-events: all;
  box-shadow:
    0px 0px 16px -4px rgba($gray900, 0.16),
    0px 4px 6px -2px rgba($gray900, 0.05);
  min-height: fit-content;
  outline: none;
  width: 348px;
  @apply flex flex-col border border-solid rounded-lg border-important-border bg-important-bg text-text-default gap-6;
  .header {
    @apply flex justify-between items-center px-4 pt-4;

    .selected-month-year {
      color: $selectedColor;
      letter-spacing: 0.1px;
      font-weight: 500;
      font-size: 18px;
      line-height: 21px;
      word-spacing: 12px;
    }

    .arrows {
      @apply flex gap-4 select-none;

      em.custom-icons {
        @apply flex w-6 h-6;
      }
    }
  }

  .calendar-grid-wrap {
    padding: 0px 10px 16px 10px;
    font-family: $primaryFontFamily;
    display: grid;
    grid-template-columns: 40px 40px 40px 40px 40px 40px 40px;
    column-gap: 8px;

    .hidden-input {
      line-height: 0;
      height: 0;
      margin: 0;
      border: none;
      position: absolute;
      outline: none;
    }

    .day-label {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 12px;
      font-size: 14px;
      line-height: 19px;
      letter-spacing: 0.3px;
      @apply text-text-default;
    }

    .day {
      display: flex;
      justify-content: center;
      align-items: center;
      box-sizing: border-box;
      height: 40px;
      letter-spacing: 0.1px;
      font-size: 16px;
      line-height: 24px;
      user-select: none;
      border-radius: 50%;
      @apply text-text-default;

      &:hover {
        @apply bg-important-hover;
      }

      &.other-month {
        @apply text-text-placeholder;
      }

      &.selected {
        background-color: $selectedColor;
        color: $white;
      }

      &.focused {
        border: 1px solid $selectedColor;
      }
    }
  }
  .time-reminder-wrap {
    display: flex;
    justify-content: space-between;
    padding: 16px 16px 26px;

    .time-picker-wrap {
      .time-label {
        font-weight: 500;
        font-size: 18px;
        line-height: 21px;
        word-spacing: 12px;
        margin-top: 16px;
      }

      .arrows {
        height: 24px;
        user-select: none;
        margin-top: 13px;
        @apply text-icon-default;

        em {
          &:first-child {
            margin-right: 16px;
          }
        }
      }
    }

    .calendar-grid-wrap {
      padding: 0px 10px 16px 10px;
      font-family: $primaryFontFamily;
      display: grid;
      grid-template-columns: 40px 40px 40px 40px 40px 40px 40px;
      column-gap: 8px;

      .hidden-input {
        line-height: 0;
        height: 0;
        margin: 0;
        border: none;
        position: absolute;
        outline: none;
      }

      .day-label {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 12px;
        font-size: 14px;
        line-height: 19px;
        letter-spacing: 0.3px;
        @apply text-text-default;
      }

      .day {
        display: flex;
        justify-content: center;
        align-items: center;
        box-sizing: border-box;
        height: 40px;
        letter-spacing: 0.1px;
        color: $gray600;
      }

      .time-picker {
        box-sizing: border-box;
        height: 44px;
        border: 1px solid $gray300;
        border-radius: 8px;
        font-size: 14px;
        line-height: 20px;
        color: $gray600;
        padding: 0px 14px;
        margin-top: 8px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        box-shadow: 0px 1px 2px rgba($gray900, 0.05);

        &.time {
          width: 130px;
        }
      }

      &.reminder {
        .time-picker {
          width: 170px;
        }
      }

      .time-dropdown {
        margin-top: 4px;
        height: 247px;
      }
    }
  }
}

[data-focused-date='true'] {
  border: 1px solid $selectedColor;
  border: 1px solid -moz-var(--selectedColor);
}

.time-picker-section {
  @apply p-4 flex justify-between gap-4;

  .time-inputs {
    @apply flex items-center gap-2;

    .time-input-group {
      @apply flex flex-col items-center;

      label {
        @apply text-sm text-text-default mb-2;
      }

      .time-input {
        @apply h-9 w-[50px] border border-solid border-standard-border bg-standard-bg text-center text-base text-text-default rounded;

        &:focus {
          outline: none;
          border-color: $selectedColor;
        }

        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      }
    }

    .time-separator {
      font-size: 24px;
      font-weight: 500;
      margin-top: 20px;
      @apply text-text-default;
    }
  }

  .delete-icon {
    @apply flex w-6 h-6;
  }
}
</style>
